/**
 * BuildingGroupHeader.tsx
 *
 * Accordion group header rendered per-building in the "Per gebouw"
 * view of EnergyPerformanceUniversalTable.
 *
 * Columns mirror the data-row column structure (LOCATIE / TYPE / HUIDIGE EPC
 * / EPC EVOLUTIE / LABELS / GEÏNVESTEERD / NIEUWE EPC NODIG) so the building
 * summary chips line up visually with the unit rows beneath each accordion.
 */
import { useMemo } from "react";
import { Buildings, Warning } from "@phosphor-icons/react";
import {
  EPCData,
  EPCLabel,
  getEPCScore,
  getEPCColor,
  getDisplayLabel,
} from "shared/components/maintenance/hooks/useEnergyPerformanceData";
import { InvestmentMode } from "shared/components/maintenance/components/EnergyHeroKpiCards";
import { formatEuroCompact } from "shared/components/renovation/helpers/formatCurrency";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ─── Score → EPCLabel reverse-lookup ─────────────────────────────────────────

const SCORE_TO_LABEL: Array<[number, EPCLabel]> = [
  [7.5, EPCLabel.A_PLUS_PLUS],
  [6.5, EPCLabel.A_PLUS],
  [5.5, EPCLabel.A],
  [4.5, EPCLabel.B],
  [3.5, EPCLabel.C],
  [2.5, EPCLabel.D],
  [1.5, EPCLabel.E],
  [0.5, EPCLabel.F],
  [0, EPCLabel.G],
];

function scoreToLabel(score: number): EPCLabel {
  for (const [threshold, label] of SCORE_TO_LABEL) {
    if (score >= threshold) return label;
  }
  return EPCLabel.G;
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface BuildingGroupHeaderProps {
  buildingId: string;
  rows: EPCData[];
  investmentMode: InvestmentMode;
  /** CSS grid-template-columns string passed by UniversalTable so the header
   *  aligns with the data rows underneath. Falls back to a single-row flex
   *  layout when absent (test environments etc.). */
  templateColumns?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function BuildingGroupHeader({
  buildingId: _buildingId,
  rows,
  investmentMode,
  templateColumns,
}: BuildingGroupHeaderProps) {
  const buildingLocation =
    rows[0]?.buildingLocation || rows[0]?.unitLocation || "Onbekend";

  // Avg EPC label — only rows with a scoreable EPC
  const avgEPCLabel = useMemo<EPCLabel | null>(() => {
    const scoreable = rows.filter(
      (r) =>
        r.currentEPC &&
        r.currentEPC.label !== EPCLabel.ONTBREKEND &&
        r.currentEPC.label !== EPCLabel.UNKNOWN &&
        r.currentEPC.label !== EPCLabel.NIET_VAN_TOEPASSING &&
        r.currentEPC.label !== EPCLabel.GEMEENSCHAPPELIJKE_DELEN,
    );
    if (scoreable.length === 0) return null;
    const avg =
      scoreable.reduce((sum, r) => sum + getEPCScore(r.currentEPC!.label), 0) /
      scoreable.length;
    return scoreToLabel(avg);
  }, [rows]);

  // Total invested per the active mode
  const totalInvested = useMemo(
    () =>
      rows.reduce(
        (sum, r) =>
          sum +
          (investmentMode === "planned"
            ? r.investment.plannedTotal
            : r.investment.actualTotal),
        0,
      ),
    [rows, investmentMode],
  );

  const actionCount = rows.filter((r) => r.epcRefreshSuggested).length;

  const hasGemeenschap = rows.some((r) =>
    r.unitId?.endsWith("-gemeenschap"),
  );
  const unitLabel = hasGemeenschap
    ? `${rows.length - 1} units + gemeenschap`
    : `${rows.length} units`;

  const scoreableCount = rows.filter(
    (r) =>
      r.currentEPC &&
      r.currentEPC.label !== EPCLabel.ONTBREKEND &&
      r.currentEPC.label !== EPCLabel.UNKNOWN &&
      r.currentEPC.label !== EPCLabel.NIET_VAN_TOEPASSING &&
      r.currentEPC.label !== EPCLabel.GEMEENSCHAPPELIJKE_DELEN,
  ).length;

  const investedModeLabel =
    investmentMode === "actueel" ? "actueel" : "planned";
  const investedTooltip =
    investmentMode === "actueel"
      ? "Op basis van alle ingeladen facturen (Concept, Ontvangen en Goedgekeurd) — facturen met status 'Afgewezen' zijn uitgesloten."
      : "Op basis van toegewezen budget via investeringsposten (planned).";

  // ─── Column cells (one per data column, mirroring COLUMNS order) ────────

  const locationCell = (
    <div className="flex min-w-0 items-center gap-1.5">
      <Buildings
        size={16}
        weight="duotone"
        className="text-neutral-secondary-mid flex-shrink-0"
      />
      <span className="text-neutral-primary truncate font-semibold">
        {buildingLocation}
      </span>
    </div>
  );

  const typeCell = (
    <span className="text-neutral-secondary-mid text-xs">{unitLabel}</span>
  );

  const epcCell = (
    <div className="flex min-w-0 items-center gap-2">
      <span className="text-neutral-secondary-mid flex-shrink-0 text-xs">
        Gemiddeld EPC in gebouw
      </span>
      {avgEPCLabel ? (
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className={`inline-block cursor-help rounded px-2 py-0.5 text-xs font-semibold ${getEPCColor(avgEPCLabel)}`}
              >
                {getDisplayLabel(avgEPCLabel)}
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="text-xs">
                Gemiddeld EPC label van {scoreableCount}{" "}
                {scoreableCount === 1 ? "unit" : "units"} met een EPC. Berekend
                als rekenkundig gemiddelde van de scores (A++=8 … G=0), terug
                gemapt naar een letter.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <span className="text-neutral-secondary-mid text-xs">Geen EPC</span>
      )}
    </div>
  );

  const investedCell = (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`cursor-help text-xs font-medium ${totalInvested > 0 ? "text-neutral-primary" : "text-neutral-secondary-mid"}`}
          >
            {totalInvested > 0 ? formatEuroCompact(totalInvested) : "€ 0"}{" "}
            <span className="font-normal text-neutral-secondary-mid">
              ({investedModeLabel})
            </span>
          </span>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          <p className="text-xs">{investedTooltip}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );

  const actionCell =
    actionCount > 0 ? (
      <span className="flex items-center gap-1 rounded bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-700 w-fit">
        <Warning size={12} weight="fill" />
        {actionCount} EPC te updaten
      </span>
    ) : (
      <span className="text-neutral-secondary-mid text-xs">Geen acties</span>
    );

  const cellWrap = "flex min-w-0 items-center";

  // ─── Render ────────────────────────────────────────────────────────────

  // When templateColumns is provided by UniversalTable, render as a 7-column
  // grid that matches the data rows. Otherwise fall back to a simple flex.
  if (templateColumns) {
    return (
      <div
        className="grid min-w-0 flex-1 items-center text-sm"
        style={{ gridTemplateColumns: templateColumns }}
      >
        <div className={`${cellWrap} pr-2`}>{locationCell}</div>
        <div className={`${cellWrap} pr-2`}>{typeCell}</div>
        <div className={`${cellWrap} pr-2`}>{epcCell}</div>
        <div className={`${cellWrap} pr-2`}>{/* EPC EVOLUTIE — n/a */}</div>
        <div className={`${cellWrap} pr-2`}>{/* LABELS — n/a */}</div>
        <div className={`${cellWrap} pr-2`}>{investedCell}</div>
        <div className={`${cellWrap} pr-2`}>{actionCell}</div>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-3 py-0.5 text-sm">
      <div className="flex min-w-0 flex-1 items-center gap-1.5">{locationCell}</div>
      {epcCell}
      {investedCell}
      {actionCell}
      <span className="text-neutral-secondary-mid flex-shrink-0 text-xs">
        {unitLabel}
      </span>
    </div>
  );
}

export default BuildingGroupHeader;
